import { WeatherError, WeatherErrorType, WeatherData, LocationData, WeatherCondition } from '../types';

export interface ErrorHandlerOptions {
  enableLogging?: boolean;
  enableRetry?: boolean;
  fallbackData?: unknown;
  context?: string;
}

export interface FallbackWeatherData extends WeatherData {
  isFallback: boolean;
  fallbackReason: string;
}

export interface FallbackLocationData extends LocationData {
  isFallback: boolean;
  fallbackReason: string;
}

export class WeatherErrorHandler {
  private static readonly DEFAULT_OPTIONS: ErrorHandlerOptions = {
    enableLogging: true,
    enableRetry: true,
    fallbackData: null,
  };

  // Default fallback data for when all APIs fail
  private static readonly DEFAULT_WEATHER_DATA: Omit<FallbackWeatherData, 'isFallback' | 'fallbackReason'> = {
    temperature: 20,
    condition: 'default' as WeatherCondition,
    description: 'Condiciones moderadas',
    humidity: 50,
    windSpeed: 0,
    location: {
      city: 'Madrid',
      country: 'España',
      coordinates: { lat: 40.4168, lon: -3.7038 }
    },
    timestamp: new Date()
  };

  private static readonly DEFAULT_LOCATION_DATA: Omit<FallbackLocationData, 'isFallback' | 'fallbackReason'> = {
    ip: '0.0.0.0',
    city: 'Madrid',
    region: 'Madrid',
    country: 'España',
    coordinates: {
      latitude: 40.4168,
      longitude: -3.7038,
    },
    timezone: 'Europe/Madrid',
  };

  static handle(
    error: Error | WeatherError,
    context: string,
    options: ErrorHandlerOptions = {}
  ): {
    shouldRetry: boolean;
    fallbackData: FallbackWeatherData | FallbackLocationData | unknown;
    userMessage: string;
    severity: 'low' | 'medium' | 'high';
    recoveryAction?: string;
  } {
    const config = { ...this.DEFAULT_OPTIONS, ...options };

    if (config.enableLogging) {
      console.error(`Weather service error in ${context}:`, error);
      
      // Enhanced logging with context
      this.logErrorDetails(error, context, config);
    }

    if (error instanceof WeatherError) {
      return this.handleWeatherError(error, config);
    }

    return this.handleGenericError(error, config, context);
  }

  private static logErrorDetails(
    error: Error | WeatherError, 
    context: string, 
    options: ErrorHandlerOptions
  ): void {
    const errorDetails = {
      timestamp: new Date().toISOString(),
      context,
      errorType: error instanceof WeatherError ? error.type : 'generic',
      message: error.message,
      stack: error.stack,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      url: typeof window !== 'undefined' ? window.location.href : 'unknown',
      options
    };

    // In production, this could be sent to an error tracking service
    console.group('🌤️ Weather Service Error Details');
    console.table(errorDetails);
    console.groupEnd();
  }

  private static handleWeatherError(
    error: WeatherError,
    options: ErrorHandlerOptions
  ): {
    shouldRetry: boolean;
    fallbackData: unknown;
    userMessage: string;
    severity: 'low' | 'medium' | 'high';
    recoveryAction?: string;
  } {
    switch (error.type) {
      case WeatherErrorType.LOCATION_DETECTION_FAILED:
        return {
          shouldRetry: false,
          fallbackData: options.fallbackData || this.createFallbackLocationData('location_api_failed'),
          userMessage: 'No se pudo detectar tu ubicación. Usando ubicación por defecto.',
          severity: 'low',
          recoveryAction: 'using_default_location'
        };

      case WeatherErrorType.WEATHER_API_FAILED:
        return {
          shouldRetry: options.enableRetry || false,
          fallbackData: options.fallbackData || this.createFallbackWeatherData('weather_api_failed'),
          userMessage: 'No se pudo obtener información del clima en este momento.',
          severity: 'medium',
          recoveryAction: 'retry_with_fallback'
        };

      case WeatherErrorType.API_LIMIT_EXCEEDED:
        return {
          shouldRetry: false,
          fallbackData: options.fallbackData || this.createFallbackWeatherData('api_limit_exceeded'),
          userMessage: 'Límite de consultas de clima alcanzado. Usando datos por defecto.',
          severity: 'high',
          recoveryAction: 'use_cached_or_default'
        };

      case WeatherErrorType.NETWORK_ERROR:
        return {
          shouldRetry: options.enableRetry || false,
          fallbackData: options.fallbackData,
          userMessage: 'Problema de conexión. Verificando datos guardados.',
          severity: 'medium',
          recoveryAction: 'retry_with_cache'
        };

      default:
        return {
          shouldRetry: false,
          fallbackData: options.fallbackData || this.createFallbackWeatherData('unknown_error'),
          userMessage: 'Error inesperado al obtener información del clima.',
          severity: 'high',
          recoveryAction: 'use_default_data'
        };
    }
  }

  private static handleGenericError(
    error: Error,
    options: ErrorHandlerOptions,
    context: string
  ): {
    shouldRetry: boolean;
    fallbackData: unknown;
    userMessage: string;
    severity: 'low' | 'medium' | 'high';
    recoveryAction?: string;
  } {
    // Enhanced error classification
    const isNetworkError = 
      error.message.includes('fetch') ||
      error.message.includes('network') ||
      error.message.includes('timeout') ||
      error.name === 'AbortError' ||
      error.name === 'TypeError';

    const isTimeoutError = 
      error.message.includes('timeout') ||
      error.name === 'AbortError';

    const isParseError = 
      error.message.includes('JSON') ||
      error.message.includes('parse') ||
      error.name === 'SyntaxError';

    // Determine appropriate fallback data based on context
    const fallbackData = options.fallbackData || 
      (context.includes('location') 
        ? this.createFallbackLocationData('generic_error')
        : this.createFallbackWeatherData('generic_error'));

    if (isTimeoutError) {
      return {
        shouldRetry: options.enableRetry || false,
        fallbackData,
        userMessage: 'La conexión está tardando más de lo esperado.',
        severity: 'medium',
        recoveryAction: 'retry_with_timeout'
      };
    }

    if (isParseError) {
      return {
        shouldRetry: false,
        fallbackData,
        userMessage: 'Error al procesar la respuesta del servidor.',
        severity: 'high',
        recoveryAction: 'use_fallback_data'
      };
    }

    if (isNetworkError) {
      return {
        shouldRetry: options.enableRetry || false,
        fallbackData,
        userMessage: 'Problema de conexión. Intentando reconectar...',
        severity: 'medium',
        recoveryAction: 'retry_with_fallback'
      };
    }

    return {
      shouldRetry: false,
      fallbackData,
      userMessage: 'Error inesperado. Usando datos por defecto.',
      severity: 'high',
      recoveryAction: 'use_default_data'
    };
  }

  static createUserFriendlyMessage(error: Error | WeatherError): string {
    if (error instanceof WeatherError) {
      switch (error.type) {
        case WeatherErrorType.LOCATION_DETECTION_FAILED:
          return 'No pudimos detectar tu ubicación automáticamente.';
        case WeatherErrorType.WEATHER_API_FAILED:
          return 'El servicio de clima no está disponible en este momento.';
        case WeatherErrorType.API_LIMIT_EXCEEDED:
          return 'Hemos alcanzado el límite de consultas de clima por hoy.';
        case WeatherErrorType.NETWORK_ERROR:
          return 'Hay un problema con tu conexión a internet.';
        default:
          return 'Ocurrió un problema inesperado con el clima.';
      }
    }

    return 'Ocurrió un error inesperado.';
  }

  static shouldShowToUser(error: Error | WeatherError): boolean {
    // Don't show technical errors to users, only user-friendly ones
    if (error instanceof WeatherError) {
      return error.type !== WeatherErrorType.NETWORK_ERROR;
    }
    return false;
  }

  static createFallbackWeatherData(reason: string): FallbackWeatherData {
    return {
      ...this.DEFAULT_WEATHER_DATA,
      isFallback: true,
      fallbackReason: reason,
      timestamp: new Date()
    };
  }

  static createFallbackLocationData(reason: string): FallbackLocationData {
    return {
      ...this.DEFAULT_LOCATION_DATA,
      isFallback: true,
      fallbackReason: reason
    };
  }

  static getRecoveryStrategies(errorType: WeatherErrorType): string[] {
    const strategies: Record<WeatherErrorType, string[]> = {
      [WeatherErrorType.LOCATION_DETECTION_FAILED]: [
        'Use default location (Madrid, Spain)',
        'Try browser geolocation API as fallback',
        'Allow user to manually set location'
      ],
      [WeatherErrorType.WEATHER_API_FAILED]: [
        'Check for cached weather data',
        'Try alternative weather API',
        'Use default weather conditions',
        'Retry with exponential backoff'
      ],
      [WeatherErrorType.API_LIMIT_EXCEEDED]: [
        'Use cached data if available',
        'Switch to alternative API provider',
        'Extend cache duration',
        'Show default weather state'
      ],
      [WeatherErrorType.NETWORK_ERROR]: [
        'Retry with exponential backoff',
        'Check for cached data',
        'Show offline mode',
        'Use default fallback data'
      ]
    };

    return strategies[errorType] || ['Use default fallback data'];
  }

  static isRecoverableError(error: Error | WeatherError): boolean {
    if (error instanceof WeatherError) {
      return [
        WeatherErrorType.NETWORK_ERROR,
        WeatherErrorType.WEATHER_API_FAILED
      ].includes(error.type);
    }

    // Generic errors that might be recoverable
    return error.message.includes('fetch') || 
           error.message.includes('timeout') ||
           error.name === 'AbortError';
  }

  static getErrorSeverity(error: Error | WeatherError): 'low' | 'medium' | 'high' {
    if (error instanceof WeatherError) {
      switch (error.type) {
        case WeatherErrorType.LOCATION_DETECTION_FAILED:
          return 'low';
        case WeatherErrorType.NETWORK_ERROR:
        case WeatherErrorType.WEATHER_API_FAILED:
          return 'medium';
        case WeatherErrorType.API_LIMIT_EXCEEDED:
          return 'high';
        default:
          return 'medium';
      }
    }
    return 'medium';
  }
}